/**
 * Benutzerverwaltung (RBAC) – Rollen + User + Rechte-Matrix.
 *
 * Tabs:
 *  1. Rollen        — alle Rollen, Permissions als editierbare Matrix (Ressource × O/R/W/X)
 *  2. Benutzer      — alle User, Detail mit Rollen-Zuweisung, Mandanten-Zugriff, Override-Matrix
 *
 * Nutzt /api/v1/admin/rbac/* (nur super_admin oder users=write).
 */
import React, { useEffect, useState, useMemo } from 'react';
import { Shield, Users, Plus, Save, Trash2, Loader2, Check, Pencil, X as XIcon, Sliders, LogIn, UserPlus, KeyRound } from 'lucide-react';
import api from '@/lib/axios';
import { toast } from 'sonner';
import { formatDateTime } from '@/lib/utils';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import RoleTuningTab from './RoleTuningTab';
import { usePermissions } from '@/contexts/PermissionsContext';
import { startImpersonation } from '@/lib/impersonation';

const LEVEL_LABEL = {
  none:   { short: 'O', color: 'bg-slate-100 text-slate-400', desc: 'Kein Zugriff' },
  read:   { short: 'R', color: 'bg-sky-100 text-sky-700', desc: 'Lesen' },
  write:  { short: 'W', color: 'bg-amber-100 text-amber-700', desc: 'Bearbeiten' },
  delete: { short: 'X', color: 'bg-rose-100 text-rose-700', desc: 'Löschen / Ausführen' },
};

/** Ältere Rollen tragen die Stufe „all“; ein unbekannter Wert darf die Seite
 *  nicht abstürzen lassen, nur weil eine Kachel dafür fehlt. */
const levelLabel = (level) =>
  LEVEL_LABEL[level === 'all' ? 'delete' : level]
  || { short: '?', color: 'bg-slate-100 text-slate-500', desc: `Unbekannte Stufe: ${level}` };

export default function RBACAdminPage() {
  const [tab, setTab] = useState('rollen');
  const [resources, setResources] = useState([]);
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [r1, r2, r3, r4] = await Promise.all([
        api.get('/admin/rbac/resources'),
        api.get('/admin/rbac/roles'),
        api.get('/admin/rbac/users'),
        api.get('/tenants'),
      ]);
      setResources(r1.data);
      setRoles(r2.data);
      setUsers(r3.data);
      setTenants(r4.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Laden fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  const refreshUsersQuiet = async () => {
    try {
      const r = await api.get('/admin/rbac/users');
      setUsers(r.data);
    } catch { /* ignore */ }
  };

  return (
    <Layout>
      <div className="p-6 md:p-8 space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
            <Shield className="w-7 h-7 text-brand-red" /> Benutzerverwaltung
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Rollen, Benutzer & Rechte-Matrix (O = Kein Zugriff · R = Lesen · W = Bearbeiten · X = Löschen/Ausführen)
          </p>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="rollen" data-testid="tab-rbac-rollen">
              <Shield className="w-4 h-4 mr-1" /> Rollen ({roles.length})
            </TabsTrigger>
            <TabsTrigger value="benutzer" data-testid="tab-rbac-benutzer">
              <Users className="w-4 h-4 mr-1" /> Benutzer ({users.length})
            </TabsTrigger>
            <TabsTrigger value="tuning" data-testid="tab-rbac-tuning">
              <Sliders className="w-4 h-4 mr-1" /> Rechte-Tuning
            </TabsTrigger>
          </TabsList>

          <TabsContent value="rollen" className="mt-6">
            <RolesTab
              resources={resources}
              roles={roles}
              loading={loading}
              reload={loadAll}
            />
          </TabsContent>

          <TabsContent value="benutzer" className="mt-6">
            <UsersTab
              resources={resources}
              roles={roles}
              users={users}
              tenants={tenants}
              loading={loading}
              reload={loadAll}
              refreshUsersQuiet={refreshUsersQuiet}
              active={tab === 'benutzer'}
            />
          </TabsContent>

          <TabsContent value="tuning" className="mt-6">
            <RoleTuningTab
              resources={resources}
              roles={roles}
              reload={loadAll}
            />
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}

// -------------- Rollen-Tab --------------

function RolesTab({ resources, roles, loading, reload }) {
  const [editingRole, setEditingRole] = useState(null);  // role obj
  const [draftPerms, setDraftPerms] = useState({});      // { resource: level }
  const [newOpen, setNewOpen] = useState(false);
  const [newDraft, setNewDraft] = useState({ name: '', description: '' });
  const [saving, setSaving] = useState(false);

  const openEdit = (role) => {
    setEditingRole(role);
    const init = {};
    resources.forEach(r => { init[r.key] = 'none'; });
    (role.permissions || []).forEach(p => { init[p.resource] = p.level; });
    setDraftPerms(init);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/rbac/roles/${editingRole.id}/permissions`, {
        permissions: resources.map(r => ({ resource: r.key, level: draftPerms[r.key] || 'none' })),
      });
      toast.success(`Rolle "${editingRole.name}" gespeichert`);
      setEditingRole(null);
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally { setSaving(false); }
  };

  const createRole = async () => {
    if (!newDraft.name.trim()) return toast.error('Name fehlt');
    try {
      await api.post('/admin/rbac/roles', newDraft);
      toast.success('Rolle angelegt');
      setNewOpen(false);
      setNewDraft({ name: '', description: '' });
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Anlegen fehlgeschlagen');
    }
  };

  const deleteRole = async (role) => {
    if (role.name === 'super_admin') return toast.error('super_admin darf nicht gelöscht werden');
    if (role.user_count > 0) {
      if (!window.confirm(`Rolle "${role.name}" ist noch ${role.user_count} Usern zugeordnet. Wirklich löschen?`)) return;
    }
    try {
      await api.delete(`/admin/rbac/roles/${role.id}`);
      toast.success('Rolle gelöscht');
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Löschen fehlgeschlagen');
    }
  };

  if (loading) return <div className="text-slate-400"><Loader2 className="w-5 h-5 animate-spin" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end gap-2">
        <Button onClick={() => setNewOpen(true)} className="bg-brand-red hover:bg-brand-red/90 text-white" data-testid="rbac-new-role-btn">
          <Plus className="w-4 h-4 mr-1" /> Neue Rolle
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              <th className="text-left px-4 py-3">Rolle</th>
              <th className="text-left px-4 py-3">Beschreibung</th>
              <th className="text-center px-4 py-3">Permissions</th>
              <th className="text-center px-4 py-3">User</th>
              <th className="text-right px-4 py-3">Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {roles.map(r => (
              <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`role-row-${r.name}`}>
                <td className="px-4 py-3 font-semibold">{r.name}</td>
                <td className="px-4 py-3 text-slate-600 text-xs">{r.description || '—'}</td>
                <td className="px-4 py-3 text-center font-mono text-xs">{r.permissions.length}</td>
                <td className="px-4 py-3 text-center">{r.user_count}</td>
                <td className="px-4 py-3 text-right">
                  <Button size="sm" variant="ghost" onClick={() => openEdit(r)} data-testid={`role-edit-${r.name}`}>
                    <Pencil className="w-4 h-4 mr-1" /> Rechte bearbeiten
                  </Button>
                  {r.name !== 'super_admin' && (
                    <Button size="sm" variant="ghost" onClick={() => deleteRole(r)} className="text-rose-600">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Permissions Matrix Editor */}
      <Dialog open={!!editingRole} onOpenChange={(o) => !o && setEditingRole(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Rechte – {editingRole?.name}</DialogTitle>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-white border-b">
                <tr>
                  <th className="text-left px-3 py-2">Ressource</th>
                  <th className="text-center px-3 py-2 w-[120px]">O<br /><span className="text-[10px] font-normal text-slate-400">Kein Zugriff</span></th>
                  <th className="text-center px-3 py-2 w-[120px]">R<br /><span className="text-[10px] font-normal text-slate-400">Lesen</span></th>
                  <th className="text-center px-3 py-2 w-[120px]">W<br /><span className="text-[10px] font-normal text-slate-400">Bearbeiten</span></th>
                  <th className="text-center px-3 py-2 w-[120px]">X<br /><span className="text-[10px] font-normal text-slate-400">Löschen</span></th>
                </tr>
              </thead>
              <tbody>
                {resources.map(res => {
                  const cur = draftPerms[res.key] || 'none';
                  return (
                    <tr key={res.key} className="border-b border-slate-100">
                      <td className="px-3 py-2 font-medium text-slate-700">{res.label}<br /><span className="text-[10px] font-mono text-slate-400">{res.key}</span></td>
                      {['none', 'read', 'write', 'delete'].map(lv => (
                        <td key={lv} className="text-center">
                          <label className="inline-flex items-center justify-center w-full h-8 cursor-pointer">
                            <input
                              type="radio"
                              name={`perm-${res.key}`}
                              value={lv}
                              checked={cur === lv}
                              onChange={() => setDraftPerms({ ...draftPerms, [res.key]: lv })}
                              className="sr-only"
                              data-testid={`perm-${res.key}-${lv}`}
                            />
                            <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full font-semibold text-xs ${cur === lv ? LEVEL_LABEL[lv].color + ' ring-2 ring-brand-red/40' : 'bg-slate-50 text-slate-300 hover:bg-slate-100'}`}>
                              {LEVEL_LABEL[lv].short}
                            </span>
                          </label>
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingRole(null)} disabled={saving}><XIcon className="w-4 h-4 mr-1" /> Abbrechen</Button>
            <Button onClick={save} disabled={saving} className="bg-brand-red hover:bg-brand-red/90 text-white" data-testid="role-save-perms-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Role Dialog */}
      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Neue Rolle</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>Name *</Label>
              <Input value={newDraft.name} onChange={e => setNewDraft({ ...newDraft, name: e.target.value })} placeholder="z.B. instrumentenwart" data-testid="rbac-new-role-name" />
            </div>
            <div>
              <Label>Beschreibung</Label>
              <Textarea rows={2} value={newDraft.description} onChange={e => setNewDraft({ ...newDraft, description: e.target.value })} />
            </div>
            <p className="text-xs text-slate-500">
              Nach dem Anlegen kannst du die Rechte über „Rechte bearbeiten" zuweisen.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewOpen(false)}>Abbrechen</Button>
            <Button onClick={createRole} className="bg-brand-red hover:bg-brand-red/90 text-white" data-testid="rbac-new-role-save">Anlegen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// -------------- Benutzer-Tab --------------

const EMPTY_NEW_USER = { username: '', email: '', password: '', full_name: '', role_ids: [], tenant_ids: [], is_super_admin: false, send_welcome_email: false, person_type: null, person_id: null, person_label: '' };

const fmtDT = (s) => (s ? formatDateTime(s) || null : null);

function UsersTab({ resources, roles, users, tenants, loading, reload, refreshUsersQuiet, active }) {
  const { isSuperAdmin } = usePermissions();
  const me = useMemo(() => { try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; } }, []);
  const [openUser, setOpenUser] = useState(null);       // user obj
  const [detail, setDetail] = useState(null);           // detail fetched from API
  const [linkQuery, setLinkQuery] = useState('');
  const [linkSuggestions, setLinkSuggestions] = useState([]);

  useEffect(() => {
    if (!isSuperAdmin || !active || !refreshUsersQuiet) return undefined;
    const t = setInterval(refreshUsersQuiet, 20000);
    return () => clearInterval(t);
  }, [isSuperAdmin, active, refreshUsersQuiet]);

  useEffect(() => {
    if (!openUser || linkQuery.trim().length < 2) { setLinkSuggestions([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/admin/rbac/person-search?q=${encodeURIComponent(linkQuery.trim())}`);
        setLinkSuggestions(r.data || []);
      } catch { setLinkSuggestions([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [linkQuery, openUser]);

  const linkPerson = async (s) => {
    try {
      const r = await api.put(`/admin/rbac/users/${openUser.id}/person-link`, { person_type: s.type, person_id: s.id });
      setDetail(d => ({ ...d, person_link: r.data.person_link }));
      setLinkQuery('');
      setLinkSuggestions([]);
      toast.success(`Verknüpft mit ${s.full_name}`);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Verknüpfung fehlgeschlagen');
    }
  };

  const removePersonLink = async () => {
    try {
      await api.put(`/admin/rbac/users/${openUser.id}/person-link`, { person_type: null, person_id: null });
      setDetail(d => ({ ...d, person_link: null }));
      toast.success('Verknüpfung entfernt');
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Entfernen fehlgeschlagen');
    }
  };
  const [saving, setSaving] = useState(false);
  const [sendingReset, setSendingReset] = useState(false);
  const [tempPassword, setTempPassword] = useState('');
  const [settingPassword, setSettingPassword] = useState(false);
  const [newUserOpen, setNewUserOpen] = useState(false);
  const [newUser, setNewUser] = useState(EMPTY_NEW_USER);
  const [creating, setCreating] = useState(false);
  const [personQuery, setPersonQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);

  useEffect(() => {
    if (!newUserOpen || personQuery.trim().length < 2) { setSuggestions([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/admin/rbac/person-search?q=${encodeURIComponent(personQuery.trim())}`);
        setSuggestions(r.data || []);
      } catch { setSuggestions([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [personQuery, newUserOpen]);

  const applySuggestion = (s) => {
    const role = roles.find(r => r.name === s.type);
    const typeLabel = s.type === 'lehrer' ? 'Lehrer' : s.type === 'schueler' ? 'Schüler' : 'Erziehungsberechtigter';
    setNewUser(u => ({
      ...u,
      full_name: s.full_name,
      email: s.email || u.email,
      username: u.username.trim() || s.email || s.full_name.toLowerCase().replace(/\s+/g, '.'),
      role_ids: role && !u.role_ids.includes(role.id) ? [...u.role_ids, role.id] : u.role_ids,
      tenant_ids: s.tenant_id && tenants.some(t => t.id === s.tenant_id) && !u.tenant_ids.includes(s.tenant_id)
        ? [...u.tenant_ids, s.tenant_id] : u.tenant_ids,
      person_type: s.type,
      person_id: s.id,
      person_label: `${s.full_name} (${typeLabel})`,
    }));
    setSuggestions([]);
    setPersonQuery('');
  };
  const [draftFlags, setDraftFlags] = useState({ is_super_admin: false, is_active: true });
  const [draftProfile, setDraftProfile] = useState({ username: '', email: '', full_name: '' });

  const createUser = async () => {
    if (!newUser.username.trim() || !newUser.email.trim()) return toast.error('Benutzername und E-Mail sind Pflichtfelder');
    if ((newUser.password || '').length < 8) return toast.error('Passwort muss mindestens 8 Zeichen lang sein');
    setCreating(true);
    try {
      const r = await api.post('/admin/rbac/users', newUser);
      toast.success(`Benutzer „${newUser.username}" angelegt`);
      const we = r.data?.welcome_email;
      if (we) {
        if (we.sent) toast.success(`Willkommens-E-Mail an ${newUser.email} gesendet`);
        else toast.error(`Willkommens-E-Mail fehlgeschlagen: ${we.message}`);
      }
      setNewUserOpen(false);
      setNewUser(EMPTY_NEW_USER);
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Anlegen fehlgeschlagen');
    } finally { setCreating(false); }
  };

  const impersonate = async (u) => {
    try {
      await startImpersonation(u.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kontowechsel fehlgeschlagen');
    }
  };

  // role assignment draft
  const [draftRoles, setDraftRoles] = useState([]);     // array of role_ids
  const [draftTenants, setDraftTenants] = useState([]); // array of tenant_ids

  const openDetail = async (user) => {
    setOpenUser(user);
    setLinkQuery('');
    setLinkSuggestions([]);
    try {
      const r = await api.get(`/admin/rbac/users/${user.id}`);
      setDetail(r.data);
      setDraftRoles(roles.filter(ro => r.data.roles.includes(ro.name)).map(ro => ro.id));
      setDraftTenants((r.data.tenants || []).map(t => t.id));
      setDraftFlags({ is_super_admin: !!r.data.is_super_admin, is_active: r.data.is_active !== false });
      setDraftProfile({
        username: r.data.username || '',
        email: r.data.email || '',
        full_name: r.data.full_name || '',
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Laden fehlgeschlagen');
      setOpenUser(null);
    }
  };

  const setTempUserPassword = async () => {
    const pw = (tempPassword || '').trim();
    if (pw.length < 8) return toast.error('Passwort muss mindestens 8 Zeichen lang sein');
    setSettingPassword(true);
    try {
      await api.put(`/admin/rbac/users/${openUser.id}/password`, { password: pw });
      toast.success(`Passwort für ${openUser.username} gesetzt — beim nächsten Login muss es geändert werden`);
      setTempPassword('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Passwort konnte nicht gesetzt werden');
    } finally {
      setSettingPassword(false);
    }
  };

  const sendPasswordReset = async () => {
    if (!openUser?.id) return;
    setSendingReset(true);
    try {
      const r = await api.post(`/admin/rbac/users/${openUser.id}/send-password-reset`);
      toast.success(r.data?.message || `Reset-Link an ${openUser.email} gesendet`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reset-Mail konnte nicht gesendet werden');
    } finally {
      setSendingReset(false);
    }
  };

  const saveRoles = async () => {
    const username = (draftProfile.username || '').trim();
    const email = (draftProfile.email || '').trim();
    if (!username || !email) return toast.error('Benutzername und E-Mail sind Pflichtfelder');
    if (username.length < 3) return toast.error('Benutzername muss mindestens 3 Zeichen lang sein');
    setSaving(true);
    try {
      await api.put(`/admin/rbac/users/${openUser.id}/profile`, {
        username,
        email,
        full_name: (draftProfile.full_name || '').trim(),
      });
      await api.put(`/admin/rbac/users/${openUser.id}/roles`, { role_ids: draftRoles });
      await api.put(`/admin/rbac/users/${openUser.id}/tenants`, { tenant_ids: draftTenants });
      const flagsChanged = draftFlags.is_super_admin !== !!detail.is_super_admin || draftFlags.is_active !== (detail.is_active !== false);
      if (flagsChanged) {
        const payload = { is_active: draftFlags.is_active };
        if (isSuperAdmin) payload.is_super_admin = draftFlags.is_super_admin;
        await api.put(`/admin/rbac/users/${openUser.id}/flags`, payload);
      }
      toast.success('Gespeichert');
      setOpenUser(null);
      setDetail(null);
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally { setSaving(false); }
  };

  if (loading) return <div className="text-slate-400"><Loader2 className="w-5 h-5 animate-spin" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={() => setNewUserOpen(true)} className="bg-brand-red hover:bg-brand-red/90 text-white" data-testid="rbac-new-user-btn">
          <UserPlus className="w-4 h-4 mr-1" /> Benutzer anlegen
        </Button>
      </div>
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              <th className="text-left px-4 py-3">Benutzer</th>
              <th className="text-left px-4 py-3">E-Mail</th>
              <th className="text-left px-4 py-3">Rollen</th>
              <th className="text-left px-4 py-3">Mandanten</th>
              <th className="text-left px-4 py-3">Verknüpft mit</th>
              <th className="text-left px-4 py-3">Letzter Login</th>
              <th className="text-center px-4 py-3">Super-Admin</th>
              <th className="text-right px-4 py-3">Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => openDetail(u)} data-testid={`user-row-${u.username || u.id}`}>
                <td className="px-4 py-3 font-semibold">
                  <span className="inline-flex items-center gap-1.5">
                    {isSuperAdmin && u.is_online && (
                      <span
                        className="inline-block w-2 h-2 rounded-full bg-emerald-500 shrink-0"
                        title="Online"
                        data-testid={`user-online-${u.username || u.id}`}
                      />
                    )}
                    {u.username || u.email}
                  </span>
                  {u.is_active === false && <Badge variant="outline" className="ml-2 text-[10px] border-rose-200 bg-rose-50 text-rose-600">inaktiv</Badge>}
                  {u.full_name && <div className="text-xs text-slate-500 font-normal">{u.full_name}</div>}
                </td>
                <td className="px-4 py-3 text-slate-600 text-xs">{u.email}</td>
                <td className="px-4 py-3">
                  {u.roles.length
                    ? u.roles.map(r => <Badge key={r} variant="outline" className="mr-1 text-xs">{r}</Badge>)
                    : <span className="text-slate-300 italic text-xs">—</span>}
                </td>
                <td className="px-4 py-3 text-xs">
                  {u.tenants.length
                    ? u.tenants.map(t => <Badge key={t.id} variant="outline" className="mr-1 border-sky-200 bg-sky-50 text-sky-700">{t.slug}</Badge>)
                    : <span className="text-slate-300 italic">—</span>}
                </td>
                <td className="px-4 py-3 text-xs" data-testid={`user-person-link-${u.username || u.id}`}>
                  {u.person_link ? (
                    <Badge variant="outline" className={
                      u.person_link.type === 'lehrer' ? 'border-emerald-200 bg-emerald-50 text-emerald-700' :
                      u.person_link.type === 'schueler' ? 'border-sky-200 bg-sky-50 text-sky-700' :
                      'border-amber-200 bg-amber-50 text-amber-700'
                    }>
                      {u.person_link.name} · {u.person_link.type === 'lehrer' ? 'Lehrer' : u.person_link.type === 'schueler' ? 'Schüler' : 'Erziehungsber.'}
                    </Badge>
                  ) : (
                    <span className="text-slate-300 italic">—</span>
                  )}
                </td>
                <td className="px-4 py-3 text-xs text-slate-600" data-testid={`user-last-login-${u.username || u.id}`}>
                  {fmtDT(u.last_login_at) || <span className="text-slate-300 italic">nie</span>}
                </td>
                <td className="px-4 py-3 text-center">
                  {u.is_super_admin && <Check className="inline w-5 h-5 text-emerald-600" />}
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  {isSuperAdmin && me?.id && u.id !== me.id && (
                    <Button size="sm" variant="ghost" title="In dieses Konto wechseln"
                            className="text-brand-red hover:text-brand-red"
                            data-testid={`impersonate-btn-${u.username || u.id}`}
                            onClick={(e) => { e.stopPropagation(); impersonate(u); }}>
                      <LogIn className="w-4 h-4" />
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); openDetail(u); }}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!openUser} onOpenChange={(o) => { if (!o) { setOpenUser(null); setDetail(null); } }}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>{openUser?.username || openUser?.email}</DialogTitle>
          </DialogHeader>
          {detail ? (
            <div className="space-y-5 max-h-[70vh] overflow-y-auto">
              <section>
                <h3 className="font-semibold text-slate-800 mb-2">Stammdaten</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Benutzername *</Label>
                    <Input
                      value={draftProfile.username}
                      onChange={(e) => setDraftProfile((p) => ({ ...p, username: e.target.value }))}
                      data-testid="user-edit-username"
                    />
                  </div>
                  <div>
                    <Label>Voller Name</Label>
                    <Input
                      value={draftProfile.full_name}
                      onChange={(e) => setDraftProfile((p) => ({ ...p, full_name: e.target.value }))}
                      placeholder="z. B. Bodo Wolff"
                      data-testid="user-edit-fullname"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label>E-Mail *</Label>
                    <Input
                      type="email"
                      value={draftProfile.email}
                      onChange={(e) => setDraftProfile((p) => ({ ...p, email: e.target.value }))}
                      data-testid="user-edit-email"
                    />
                  </div>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  className="mt-3"
                  onClick={sendPasswordReset}
                  disabled={sendingReset}
                  data-testid="user-send-password-reset"
                >
                  {sendingReset ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <KeyRound className="w-4 h-4 mr-2" />}
                  Reset-Link per E-Mail senden
                </Button>
                <p className="text-xs text-slate-400 mt-1">
                  Sendet einen Link (1 Stunde gültig) an die gespeicherte E-Mail. Alte Passwörter können nicht nachgeschickt werden.
                </p>
                <div className="mt-3 flex gap-2 items-end">
                  <div className="flex-1">
                    <Label>Neues Passwort setzen (min. 8 Zeichen)</Label>
                    <Input
                      type="text"
                      autoComplete="new-password"
                      value={tempPassword}
                      onChange={(e) => setTempPassword(e.target.value)}
                      placeholder="temporäres Passwort"
                      data-testid="user-set-password-input"
                    />
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={setTempUserPassword}
                    disabled={settingPassword}
                    data-testid="user-set-password-btn"
                  >
                    {settingPassword ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <KeyRound className="w-4 h-4 mr-2" />}
                    Passwort setzen
                  </Button>
                </div>
                <p className="text-xs text-slate-400 mt-1">
                  Danach Login mit Benutzername <strong>{openUser?.username}</strong> oder der E-Mail. Beim ersten Login muss das Passwort geändert werden.
                </p>
              </section>

              {/* Status-Flags */}
              <section className="flex flex-wrap gap-6">
                {isSuperAdmin && (
                  <label className="flex items-center gap-2">
                    <Switch checked={draftFlags.is_super_admin}
                            disabled={openUser?.id === me?.id}
                            onCheckedChange={(v) => setDraftFlags(f => ({ ...f, is_super_admin: v }))}
                            data-testid="user-superadmin-switch" />
                    <span className="text-sm font-medium text-slate-700">Super-Admin</span>
                    {openUser?.id === me?.id && <span className="text-xs text-slate-400">(eigener Status nicht änderbar)</span>}
                  </label>
                )}
                <label className="flex items-center gap-2">
                  <Switch checked={draftFlags.is_active}
                          disabled={openUser?.id === me?.id}
                          onCheckedChange={(v) => setDraftFlags(f => ({ ...f, is_active: v }))}
                          data-testid="user-active-switch" />
                  <span className="text-sm font-medium text-slate-700">Aktiv</span>
                </label>
              </section>

              {/* Rollen */}
              <section>
                <h3 className="font-semibold text-slate-800 mb-2">Rollen</h3>
                <div className="flex flex-wrap gap-2">
                  {roles.map(r => {
                    const checked = draftRoles.includes(r.id);
                    return (
                      <label key={r.id} className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border cursor-pointer ${checked ? 'bg-brand-red/5 border-brand-red/30 text-brand-red' : 'bg-slate-50 border-slate-200 text-slate-600'}`}>
                        <input type="checkbox" checked={checked} onChange={(e) => {
                          setDraftRoles(e.target.checked ? [...draftRoles, r.id] : draftRoles.filter(x => x !== r.id));
                        }} data-testid={`user-role-${r.name}`} />
                        <span className="text-sm font-medium">{r.name}</span>
                      </label>
                    );
                  })}
                </div>
              </section>

              {/* Mandanten */}
              <section>
                <h3 className="font-semibold text-slate-800 mb-2">Mandanten-Zugriff</h3>
                <div className="flex flex-wrap gap-2">
                  {tenants.map(t => {
                    const checked = draftTenants.includes(t.id);
                    return (
                      <label key={t.id} className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border cursor-pointer ${checked ? 'bg-sky-50 border-sky-300 text-sky-700' : 'bg-slate-50 border-slate-200 text-slate-600'}`}>
                        <input type="checkbox" checked={checked} onChange={(e) => {
                          setDraftTenants(e.target.checked ? [...draftTenants, t.id] : draftTenants.filter(x => x !== t.id));
                        }} data-testid={`user-tenant-${t.slug}`} />
                        <span className="text-sm font-medium">{t.slug}<span className="text-slate-400 text-xs ml-1">{t.name}</span></span>
                      </label>
                    );
                  })}
                </div>
              </section>

              {/* Personen-Verknüpfung */}
              <section data-testid="person-link-section">
                <h3 className="font-semibold text-slate-800 mb-2">Personen-Verknüpfung</h3>
                {detail.person_link ? (
                  <div className="flex items-center gap-2 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-lg" data-testid="person-link-current">
                    <Check className="w-4 h-4 text-emerald-600 shrink-0" />
                    <span className="text-sm text-emerald-800">
                      Verknüpft mit: <strong>{detail.person_link.name}</strong>{' '}
                      ({detail.person_link.type === 'lehrer' ? 'Lehrer' : detail.person_link.type === 'schueler' ? 'Schüler' : 'Erziehungsberechtigter'})
                    </span>
                    <button
                      type="button"
                      onClick={removePersonLink}
                      className="ml-auto text-emerald-700 hover:text-red-600"
                      data-testid="person-link-unlink"
                      title="Verknüpfung entfernen"
                    >
                      <XIcon className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div>
                    <Input
                      value={linkQuery}
                      onChange={(e) => setLinkQuery(e.target.value)}
                      placeholder="Person suchen (Name oder E-Mail) zum Verknüpfen..."
                      data-testid="person-link-search"
                    />
                    {linkSuggestions.length > 0 && (
                      <div className="mt-2 border border-brand-red/20 bg-brand-red/5 rounded-lg p-2 max-h-36 overflow-y-auto space-y-1">
                        {linkSuggestions.map((s) => (
                          <button
                            key={`${s.type}-${s.id}`}
                            type="button"
                            onClick={() => linkPerson(s)}
                            className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md bg-white border border-slate-200 hover:border-brand-red/40 hover:bg-brand-red/5 text-left"
                            data-testid={`person-link-suggestion-${s.type}-${s.id}`}
                          >
                            <span className="text-sm font-medium text-slate-800">{s.full_name}</span>
                            <Badge variant="outline" className={
                              s.type === 'lehrer' ? 'text-emerald-700 border-emerald-300' :
                              s.type === 'schueler' ? 'text-sky-700 border-sky-300' : 'text-amber-700 border-amber-300'
                            }>
                              {s.type === 'lehrer' ? 'Lehrer' : s.type === 'schueler' ? 'Schüler' : 'Erziehungsber.'}
                            </Badge>
                            {s.email && <span className="text-xs text-slate-500 truncate">{s.email}</span>}
                          </button>
                        ))}
                      </div>
                    )}
                    <p className="text-xs text-slate-400 mt-1">Portale zeigen über die Verknüpfung automatisch die richtigen Daten.</p>
                  </div>
                )}
              </section>

              {/* Login-Historie */}
              <section>
                <h3 className="font-semibold text-slate-800 mb-2">Letzte Anmeldungen</h3>
                {(detail.last_logins || []).length === 0 ? (
                  <p className="text-sm text-slate-400 italic">Noch keine Anmeldungen protokolliert.</p>
                ) : (
                  <div className="border border-slate-200 rounded-lg overflow-hidden max-h-[220px] overflow-y-auto">
                    <table className="w-full text-xs" data-testid="user-login-history">
                      <thead className="bg-slate-50 sticky top-0">
                        <tr>
                          <th className="text-left px-3 py-2">Zeitpunkt</th>
                          <th className="text-left px-3 py-2">Status</th>
                          <th className="text-left px-3 py-2">IP</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detail.last_logins.map((l, i) => (
                          <tr key={i} className="border-t border-slate-100">
                            <td className="px-3 py-1.5">{fmtDT(l.at)}</td>
                            <td className="px-3 py-1.5">
                              {l.success
                                ? <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-700 text-[10px]">Erfolgreich</Badge>
                                : <Badge variant="outline" className="border-rose-200 bg-rose-50 text-rose-600 text-[10px]">{l.reason || 'Fehlgeschlagen'}</Badge>}
                            </td>
                            <td className="px-3 py-1.5 text-slate-500">{l.ip || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>

              {/* Effektive Permissions pro Mandant */}
              {Object.keys(detail.effective_by_tenant || {}).length > 0 && (
                <section>
                  <h3 className="font-semibold text-slate-800 mb-2">Effektive Rechte pro Mandant <span className="text-xs font-normal text-slate-500">(Rolle + Override)</span></h3>
                  <div className="overflow-x-auto max-h-[300px] overflow-y-auto border border-slate-200 rounded-lg">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50 sticky top-0">
                        <tr>
                          <th className="text-left px-3 py-2">Ressource</th>
                          {Object.keys(detail.effective_by_tenant).map(tid => {
                            const t = tenants.find(x => x.id === tid);
                            return <th key={tid} className="text-center px-3 py-2">{t?.slug || tid.slice(0, 6)}</th>;
                          })}
                        </tr>
                      </thead>
                      <tbody>
                        {resources.map(res => (
                          <tr key={res.key} className="border-t border-slate-100">
                            <td className="px-3 py-1.5 font-medium">{res.label}</td>
                            {Object.keys(detail.effective_by_tenant).map(tid => {
                              const badge = levelLabel(detail.effective_by_tenant[tid][res.key] || 'none');
                              return (
                                <td key={tid} className="text-center px-3 py-1.5">
                                  <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-[10px] font-semibold ${badge.color}`} title={badge.desc}>
                                    {badge.short}
                                  </span>
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              )}
            </div>
          ) : (
            <div className="py-8 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline" /> Lade…</div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setOpenUser(null); setDetail(null); }} disabled={saving}>Abbrechen</Button>
            <Button onClick={saveRoles} disabled={saving || !detail} className="bg-brand-red hover:bg-brand-red/90 text-white" data-testid="user-save-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Benutzer anlegen */}
      <Dialog open={newUserOpen} onOpenChange={(o) => { setNewUserOpen(o); if (!o) setNewUser(EMPTY_NEW_USER); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><UserPlus className="w-5 h-5 text-brand-red" /> Neuen Benutzer anlegen</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Benutzername *</Label>
                <Input value={newUser.username} onChange={(e) => { setNewUser(u => ({ ...u, username: e.target.value })); setPersonQuery(e.target.value); }}
                       placeholder="z. B. m.mueller" data-testid="new-user-username" />
              </div>
              <div>
                <Label>Voller Name</Label>
                <Input value={newUser.full_name} onChange={(e) => { setNewUser(u => ({ ...u, full_name: e.target.value })); setPersonQuery(e.target.value); }}
                       placeholder="Max Müller" data-testid="new-user-fullname" />
              </div>
              <div>
                <Label>E-Mail *</Label>
                <Input type="email" value={newUser.email} onChange={(e) => setNewUser(u => ({ ...u, email: e.target.value }))}
                       placeholder="max@musikschule.de" data-testid="new-user-email" />
              </div>
              <div>
                <Label>Passwort * <span className="text-xs text-slate-400 font-normal">(min. 8 Zeichen)</span></Label>
                <Input type="password" value={newUser.password} onChange={(e) => setNewUser(u => ({ ...u, password: e.target.value }))}
                       data-testid="new-user-password" />
              </div>
            </div>

            {newUser.person_id && (
              <div className="flex items-center gap-2 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-lg" data-testid="person-link-chip">
                <Check className="w-4 h-4 text-emerald-600 shrink-0" />
                <span className="text-sm text-emerald-800">
                  Wird verknüpft mit: <strong>{newUser.person_label}</strong> — Portale zeigen automatisch die richtigen Daten
                </span>
                <button
                  type="button"
                  onClick={() => setNewUser(u => ({ ...u, person_type: null, person_id: null, person_label: '' }))}
                  className="ml-auto text-emerald-700 hover:text-emerald-900"
                  data-testid="person-link-remove"
                  title="Verknüpfung entfernen"
                >
                  <XIcon className="w-4 h-4" />
                </button>
              </div>
            )}

            {suggestions.length > 0 && (
              <div className="border border-brand-red/20 bg-brand-red/5 rounded-lg p-2" data-testid="person-suggestions">
                <p className="text-xs text-slate-500 px-1 pb-1">In der Datenbank gefunden — klicken zum Vorausfüllen:</p>
                <div className="max-h-40 overflow-y-auto space-y-1">
                  {suggestions.map((s) => (
                    <button
                      key={`${s.type}-${s.id}`}
                      type="button"
                      onClick={() => applySuggestion(s)}
                      className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md bg-white border border-slate-200 hover:border-brand-red/40 hover:bg-brand-red/5 text-left"
                      data-testid={`person-suggestion-${s.type}-${s.id}`}
                    >
                      <span className="text-sm font-medium text-slate-800">{s.full_name}</span>
                      <Badge variant="outline" className={
                        s.type === 'lehrer' ? 'text-emerald-700 border-emerald-300' :
                        s.type === 'schueler' ? 'text-sky-700 border-sky-300' : 'text-amber-700 border-amber-300'
                      }>
                        {s.type === 'lehrer' ? 'Lehrer' : s.type === 'schueler' ? 'Schüler' : 'Erziehungsber.'}
                      </Badge>
                      {s.email && <span className="text-xs text-slate-500 truncate">{s.email}</span>}
                      {s.has_account && (
                        <Badge variant="outline" className="ml-auto text-red-600 border-red-300 shrink-0">hat bereits Konto</Badge>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div>
              <Label className="mb-2 block">Rollen</Label>
              <div className="flex flex-wrap gap-2">
                {roles.map(r => {
                  const checked = newUser.role_ids.includes(r.id);
                  return (
                    <label key={r.id} className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border cursor-pointer ${checked ? 'bg-brand-red/5 border-brand-red/30 text-brand-red' : 'bg-slate-50 border-slate-200 text-slate-600'}`}>
                      <input type="checkbox" checked={checked} onChange={(e) => {
                        setNewUser(u => ({ ...u, role_ids: e.target.checked ? [...u.role_ids, r.id] : u.role_ids.filter(x => x !== r.id) }));
                      }} data-testid={`new-user-role-${r.name}`} />
                      <span className="text-sm font-medium">{r.name}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            <div>
              <Label className="mb-2 block">Mandanten-Zugriff</Label>
              <div className="flex flex-wrap gap-2">
                {tenants.map(t => {
                  const checked = newUser.tenant_ids.includes(t.id);
                  return (
                    <label key={t.id} className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border cursor-pointer ${checked ? 'bg-sky-50 border-sky-300 text-sky-700' : 'bg-slate-50 border-slate-200 text-slate-600'}`}>
                      <input type="checkbox" checked={checked} onChange={(e) => {
                        setNewUser(u => ({ ...u, tenant_ids: e.target.checked ? [...u.tenant_ids, t.id] : u.tenant_ids.filter(x => x !== t.id) }));
                      }} data-testid={`new-user-tenant-${t.slug}`} />
                      <span className="text-sm font-medium">{t.slug}<span className="text-slate-400 text-xs ml-1">{t.name}</span></span>
                    </label>
                  );
                })}
              </div>
            </div>

            <label className="flex items-center gap-2 pt-1">
              <Switch checked={newUser.send_welcome_email}
                      onCheckedChange={(v) => setNewUser(u => ({ ...u, send_welcome_email: v }))}
                      data-testid="new-user-welcome-email-switch" />
              <span className="text-sm font-medium text-slate-700">Willkommens-E-Mail senden <span className="text-xs text-slate-400 font-normal">(Zugangsdaten per E-Mail, SMTP des ersten Mandanten)</span></span>
            </label>

            {isSuperAdmin && (
              <label className="flex items-center gap-2 pt-1">
                <Switch checked={newUser.is_super_admin}
                        onCheckedChange={(v) => setNewUser(u => ({ ...u, is_super_admin: v }))}
                        data-testid="new-user-superadmin-switch" />
                <span className="text-sm font-medium text-slate-700">Super-Admin <span className="text-xs text-slate-400 font-normal">(Zugriff auf alle Mandanten & Einstellungen)</span></span>
              </label>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewUserOpen(false)} disabled={creating}>Abbrechen</Button>
            <Button onClick={createUser} disabled={creating} className="bg-brand-red hover:bg-brand-red/90 text-white" data-testid="new-user-save-btn">
              {creating ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <UserPlus className="w-4 h-4 mr-1" />} Anlegen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
